// ====================================================================
// MINIMAP + QUEST COMPASS + HOME — the wayfinding HUD
// ====================================================================
// Design plan step 4 (wayfinding: "a simple map, breadcrumbs back to the
// village hub, and fast travel home"). A small round top-down map in the
// top-right corner shows the whole ±WORLD_HALF world at once — the village
// hub at centre, the four themed zones, the outer landmarks, any active quest
// objective, and Chocky himself with a heading arrow. Because the entire world
// fits inside the disc there are no off-screen markers to chase: every
// destination is always visible, which is what young players need.
//
// Three things ride on it:
//   - the MAP itself (icons-first, per design line 37 — a K–2 reading budget);
//   - the QUEST COMPASS: active quest objectives pulse gold and the nearest one
//     draws a guide line from Chocky, with a label strip naming it;
//   - HOME: a "🏠 Home" button fast-travels to spawn, and whenever Chocky wanders
//     far from the hub the home marker pulses as a breadcrumb cue to head back.
//
// UI is DOM/canvas, never in-canvas WebGL (design plan §6/§7): the module builds
// its own elements (like game/store.js) so index.html stays untouched, and it
// reaches the world only through the ctx main.js passes in — it never imports
// core globals. No per-frame allocation on the draw path (it runs at a throttled
// cadence, but the beacon list is reused, not rebuilt here).

const SIZE = 132;                                   // css px diameter of the disc
const DPR = Math.min(2, (typeof devicePixelRatio === 'number' && devicePixelRatio) || 1);
const REDRAW_HZ = 12;                               // the map is forgiving; 12Hz is plenty

// Which named area Chocky is standing in — mirrors updateZoneVisualTarget()'s
// ±26 thresholds in main.js so the label strip agrees with the fog tint.
function areaName(x, z) {
  if (x > 26) return 'Wordville';
  if (x < -26) return 'The Grove';
  if (z > 26) return 'Obby Peak';
  if (z < -34) return 'Numberton';
  if (z < -26) return 'Village Green';
  return 'Chockytown';
}

export function initMinimap(ctx) {
  const worldHalf = ctx.worldHalf || 60;
  const R = SIZE / 2;
  const inset = 11;                                 // keep edge markers off the rim
  const scale = (R - inset) / worldHalf;            // world units → map px
  // World → map pixels. East (+x, Wordville) is right. Screen-space north in
  // Chockytown's fixed play camera corresponds to lower world z, so +z maps
  // downward; this keeps movement on the minimap aligned with movement on the
  // main screen instead of feeling vertically mirrored.
  const mapX = (x) => R + x * scale;
  const mapY = (z) => R + z * scale;

  // ---- DOM: a right-aligned column of disc + label + home button ------------
  const wrap = document.createElement('div');
  wrap.id = 'minimap';
  wrap.style.cssText =
    'display:none; position:fixed; top:64px; right:16px; z-index:6;' +
    'flex-direction:column; align-items:flex-end; gap:6px; pointer-events:none;' +
    'font:800 12px/1.1 ui-sans-serif, system-ui, sans-serif; user-select:none;';

  const canvas = document.createElement('canvas');
  canvas.width = SIZE * DPR;
  canvas.height = SIZE * DPR;
  canvas.style.cssText =
    `width:${SIZE}px; height:${SIZE}px; border-radius:50%;` +
    'box-shadow:0 6px 20px rgba(0,0,0,0.22); border:2px solid rgba(255,255,255,0.7);';

  const label = document.createElement('div');
  label.style.cssText =
    'max-width:150px; text-align:center; padding:3px 10px; border-radius:10px;' +
    'background:rgba(255,255,255,0.92); color:#1b1f2a; box-shadow:0 4px 14px rgba(0,0,0,0.12);' +
    'white-space:nowrap; overflow:hidden; text-overflow:ellipsis;';

  const homeBtn = document.createElement('button');
  homeBtn.textContent = '🏠 Home';
  homeBtn.style.cssText =
    'pointer-events:auto; cursor:pointer; font:inherit; padding:6px 14px; border:none;' +
    'border-radius:10px; background:rgba(255,210,127,0.95); color:#5a3d12;' +
    'box-shadow:0 4px 14px rgba(0,0,0,0.16);';
  homeBtn.title = 'Fast-travel back to the village';

  wrap.append(canvas, label, homeBtn);
  document.body.appendChild(wrap);

  const g = canvas.getContext('2d');
  homeBtn.addEventListener('click', () => { if (ctx.isPlaying()) ctx.goHome(); });

  let visible = false;
  let acc = 0;
  let phase = 0;                                     // drives the gentle pulses

  // A faint road cross + the two y=x diagonals, so the map reads as "roads out
  // of a hub" even before you learn the icons.
  function drawRoads() {
    g.strokeStyle = 'rgba(255,255,255,0.16)';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(mapX(-worldHalf), mapY(0)); g.lineTo(mapX(worldHalf), mapY(0));
    g.moveTo(mapX(0), mapY(-worldHalf)); g.lineTo(mapX(0), mapY(worldHalf));
    g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.09)';
    g.beginPath();
    g.moveTo(mapX(-30), mapY(-30)); g.lineTo(mapX(30), mapY(30));
    g.moveTo(mapX(-30), mapY(30)); g.lineTo(mapX(30), mapY(-30));
    g.stroke();
  }

  function drawMarker(p, homeFar) {
    const mx = mapX(p.x), my = mapY(p.z);
    if (p.icon) {
      // Zone / home markers: a coloured pip with its emoji, so it reads at a glance.
      const pulse = (p.kind === 'home' && homeFar) ? 0.6 + 0.4 * Math.sin(phase * 3) : 1;
      g.beginPath();
      g.arc(mx, my, 8, 0, Math.PI * 2);
      g.fillStyle = p.color || '#ffd27f';
      g.globalAlpha = 0.9 * pulse;
      g.fill();
      g.globalAlpha = 1;
      g.font = '11px "Apple Color Emoji","Segoe UI Emoji",ui-sans-serif';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(p.icon, mx, my + 0.5);
    } else {
      // Minor landmarks: a small dot, no label — texture, not destinations.
      g.beginPath();
      g.arc(mx, my, 3, 0, Math.PI * 2);
      g.fillStyle = p.color || 'rgba(255,255,255,0.7)';
      g.fill();
    }
  }

  function drawBeacon(b) {
    const mx = mapX(b.x), my = mapY(b.z);
    const r = 6 + 2.5 * (0.5 + 0.5 * Math.sin(phase * 4));
    g.beginPath();
    g.arc(mx, my, r, 0, Math.PI * 2);
    g.strokeStyle = 'rgba(255,210,63,0.95)';
    g.lineWidth = 2.5;
    g.stroke();
    g.beginPath();
    g.arc(mx, my, 2.5, 0, Math.PI * 2);
    g.fillStyle = '#ffd23f';
    g.fill();
  }

  function drawPlayer(p) {
    const mx = mapX(p.x), my = mapY(p.z);
    const f = ctx.getPlayerFacing ? ctx.getPlayerFacing() : 0;
    // Forward world dir (sin f, cos f) → map dir (sin f, cos f), matching
    // mapY() above so the player arrow and position move in the same direction.
    const dx = Math.sin(f), dy = Math.cos(f);
    const px = -dy, py = dx;                         // perpendicular for the base
    g.beginPath();
    g.moveTo(mx + dx * 8, my + dy * 8);
    g.lineTo(mx + px * 5 - dx * 4, my + py * 5 - dy * 4);
    g.lineTo(mx - px * 5 - dx * 4, my - py * 5 - dy * 4);
    g.closePath();
    g.fillStyle = '#fff';
    g.strokeStyle = '#1b1f2a';
    g.lineWidth = 1.5;
    g.fill();
    g.stroke();
  }

  function draw() {
    const p = ctx.getPlayerPos();
    const homeDist = Math.hypot(p.x, p.z);
    const homeFar = homeDist > 16;

    g.setTransform(DPR, 0, 0, DPR, 0, 0);
    g.clearRect(0, 0, SIZE, SIZE);

    // Clip everything to the disc.
    g.save();
    g.beginPath();
    g.arc(R, R, R - 1, 0, Math.PI * 2);
    g.clip();

    g.fillStyle = 'rgba(28,34,48,0.58)';
    g.fillRect(0, 0, SIZE, SIZE);
    drawRoads();

    for (let i = 0; i < ctx.points.length; i++) drawMarker(ctx.points[i], homeFar);

    // Active quest objectives: pulse gold, and draw a guide line from Chocky to
    // the nearest one (the "compass"). Beacons list is owned by main.js.
    const beacons = ctx.getBeacons ? ctx.getBeacons(p.x, p.z) : null;
    let nearest = null, nearestD = Infinity;
    if (beacons && beacons.length) {
      for (let i = 0; i < beacons.length; i++) {
        const b = beacons[i];
        const d = Math.hypot(b.x - p.x, b.z - p.z);
        if (d < nearestD) { nearestD = d; nearest = b; }
      }
      if (nearest) {
        g.strokeStyle = 'rgba(255,210,63,0.5)';
        g.lineWidth = 2;
        g.setLineDash([4, 4]);
        g.beginPath();
        g.moveTo(mapX(p.x), mapY(p.z));
        g.lineTo(mapX(nearest.x), mapY(nearest.z));
        g.stroke();
        g.setLineDash([]);
      }
      for (let i = 0; i < beacons.length; i++) drawBeacon(beacons[i]);
    }

    drawPlayer(p);
    g.restore();

    // Label strip: the active objective takes priority (it's why you opened the
    // map); otherwise it names the area you're standing in.
    if (nearest) {
      const m = Math.round(nearestD);
      label.textContent = `🎯 ${nearest.label} · ${m}m`;
      label.style.background = 'rgba(255,244,214,0.95)';
    } else {
      label.textContent = homeFar ? `📍 ${areaName(p.x, p.z)}` : 'Chockytown';
      label.style.background = 'rgba(255,255,255,0.92)';
    }
  }

  function update(dt) {
    if (!visible) return;
    phase += dt;
    acc += dt;
    if (acc < 1 / REDRAW_HZ) return;
    acc = 0;
    draw();
  }

  function setVisible(on) {
    visible = !!on;
    wrap.style.display = visible ? 'flex' : 'none';
    if (visible) { acc = 1; update(0.0001); }        // force an immediate first paint
  }

  function toggle() { setVisible(!visible); }
  function isVisible() { return visible; }

  return { update, setVisible, toggle, isVisible };
}
